import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton, UserAvatar, CompanyLogo } from "@/components/common";
import { BarAction, Chips, PageBar, QuietSearch } from "@/components/layout/PageBar";
import { compactMoney } from "@/lib/sk";

export const INDUSTRIES = ["Shipyard", "Ship owner", "Ship designer", "Maritime"];
export const INDUSTRY_LABEL = { Shipyard: "Verft", "Ship owner": "Rederi", "Ship designer": "Skipsdesigner", Maritime: "Maritim" };
const COUNTRIES = ["Norway", "Turkey", "Netherlands", "Poland", "Romania", "Spain", "Denmark", "Finland", "Germany", "China", "Other"];
const CUR = { Norway: "NOK", Denmark: "NOK", Turkey: "EUR", Netherlands: "EUR", Poland: "EUR", Romania: "EUR", Spain: "EUR", Finland: "EUR", Germany: "EUR", China: "USD", Other: "EUR" };

export function NewCustomerDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", industry: "Shipyard", country: "Norway", city: "", website: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", industry: "Shipyard", country: "Norway", city: "", website: "" }); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/accounts", { ...form, currency: CUR[form.country] || "EUR", legal_entity: "Skarpenord AS" });
      toast.success(`${r.data.name} opprettet`);
      onOpenChange(false);
      onCreated?.(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const field = "h-10 w-full rounded-[10px] border border-black/[0.1] bg-white px-3 text-[13.5px] outline-none focus:border-primary/50";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border-black/[0.08] sm:max-w-md" data-testid="new-customer-dialog">
        <DialogHeader><DialogTitle className="font-display text-[20px] tracking-[-0.02em]">Ny kunde</DialogTitle><DialogDescription>Bare det nødvendige. Kunder opprettes også automatisk fra forespørsler.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="c-name">Selskap</Label><Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 rounded-[10px]" autoFocus required placeholder="f.eks. Vard Søviknes" data-testid="new-customer-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Type</Label><select value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className={field} data-testid="new-customer-industry-select">{INDUSTRIES.map((i) => <option key={i} value={i}>{INDUSTRY_LABEL[i]}</option>)}</select></div>
            <div className="space-y-1.5"><Label>Land</Label><select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={field} data-testid="new-customer-country-select">{COUNTRIES.map((i) => <option key={i} value={i}>{i}</option>)}</select></div>
            <div className="space-y-1.5"><Label htmlFor="c-city">Sted</Label><Input id="c-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-10 rounded-[10px]" data-testid="new-customer-city-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="c-web">Nettsted</Label><Input id="c-web" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="verft.no" className="h-10 rounded-[10px]" data-testid="new-customer-website-input" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)} className="h-9 rounded-full px-4 text-[13px] font-medium text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" data-testid="new-customer-cancel-button">Avbryt</button>
            <button type="submit" disabled={busy || !form.name.trim()} className="h-9 rounded-full bg-[hsl(var(--brand))] px-4 text-[13px] font-medium text-white hover:bg-[hsl(var(--brand-strong))] disabled:opacity-40" data-testid="new-customer-submit-button">{busy ? "Oppretter…" : "Opprett kunde"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Customers are a view over the same projects: who we work with, what is open, what we have delivered. */
export default function CustomersPage() {
  const [accounts, setAccounts] = useState(null);
  const [projects, setProjects] = useState([]);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const industry = params.get("industry") || "all";
  const setParam = (k, v) => setParams((prev) => { const n = new URLSearchParams(prev); if (!v || v === "all") n.delete(k); else n.set(k, v); return n; }, { replace: true });
  const setQ = (v) => setParam("q", v);
  const setIndustry = (v) => setParam("industry", v);
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("new") === "1");
  const navigate = useNavigate();

  const load = () => Promise.all([api.get("/accounts"), api.get("/projects", { params: { limit: 500 } })]).then(([a, p]) => { setAccounts(a.data || []); setProjects(p.data || []); }).catch((e) => { toast.error(errMsg(e)); setAccounts([]); });
  useEffect(() => { load(); }, []);

  const byAcc = useMemo(() => {
    const m = {};
    projects.forEach((p) => { if (p.account_id) (m[p.account_id] = m[p.account_id] || []).push(p); });
    return m;
  }, [projects]);
  const industries = useMemo(() => Array.from(new Set((accounts || []).map((a) => a.industry).filter(Boolean))), [accounts]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (accounts || [])
      .filter((a) => (industry === "all" || a.industry === industry) && (!s || `${a.name} ${a.city} ${a.country} ${a.owner_name}`.toLowerCase().includes(s)))
      .sort((a, b) => (b.open_value || 0) - (a.open_value || 0) || a.name.localeCompare(b.name));
  }, [accounts, q, industry]);
  const totals = { open: (accounts || []).reduce((s, a) => s + (a.open_value || 0), 0), won: (accounts || []).reduce((s, a) => s + (a.won_revenue || 0), 0) };
  const meta = accounts ? [`${accounts.length} ${accounts.length === 1 ? "kunde" : "kunder"}`, totals.open ? `${compactMoney(totals.open)} åpent` : null, totals.won ? `${compactMoney(totals.won)} vunnet` : null].filter(Boolean).join(" · ") : null;

  return (
    <div className="mx-auto w-full max-w-[1000px] px-5 pb-16 pt-5 sm:px-8" data-testid="customers-page">
      <PageBar title="Kunder" meta={meta} testId="customers-bar" action={<BarAction onClick={() => setOpen(true)} testId="customers-new-button"><Plus className="h-4 w-4" strokeWidth={2.2} /> Ny kunde</BarAction>}>
        <QuietSearch value={q} onChange={setQ} placeholder="Søk kunde, sted, eier …" className="w-full sm:w-[260px]" testId="customers-search-input" />
        {industries.length > 1 ? <Chips value={industry} onChange={setIndustry} items={[["all", "Alle"], ...industries.map((i) => [i, INDUSTRY_LABEL[i] || i])]} label="Type" testId="customers-filter" /> : null}
      </PageBar>

      <div className="mt-4" data-testid="customers-list">
        {!accounts ? (
          <div className="space-y-3 py-2"><Skeleton className="h-12" /><Skeleton className="h-12" /><Skeleton className="h-12" /></div>
        ) : list.length ? (
          <ul className="divide-y divide-black/[0.05] border-t border-black/[0.06]">
            {list.map((a) => {
              const ps = byAcc[a.id] || [];
              const openPs = ps.filter((p) => !["won", "lost", "new"].includes(p.status));
              const facts = [a.open_value ? `${compactMoney(a.open_value)} åpent` : null, openPs.length ? `${openPs.length} ${openPs.length === 1 ? "åpen sak" : "åpne saker"}` : null, a.won_count ? `${a.won_count} ${a.won_count === 1 ? "kontrakt" : "kontrakter"}` : null].filter(Boolean);
              return (
                <li key={a.id}>
                  <button type="button" onClick={() => navigate(`/customers/${a.id}`)} className="group flex w-full items-center gap-4 rounded-[10px] px-2 py-3 text-left transition-colors hover:bg-black/[0.025] sm:px-3" data-testid={`customers-row-${a.id}`}>
                    <CompanyLogo name={a.name} domain={a.website} size="sm" className="h-9 w-9 shrink-0 rounded-[10px] p-1" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-medium leading-[1.3]" data-testid="customers-row-name">{a.name}</div>
                      <div className="mt-0.5 truncate text-[13px] text-muted-foreground">{[INDUSTRY_LABEL[a.industry] || a.industry, a.city, a.country !== "Norway" ? a.country : null].filter(Boolean).join(" · ")}</div>
                    </div>
                    <div className="hidden shrink-0 text-right text-[13.5px] text-muted-foreground sm:block" data-testid="customers-row-facts">{facts.length ? facts.join(" · ") : <span className="text-muted-foreground/50">ingen saker ennå</span>}</div>
                    <UserAvatar name={a.owner_name} initials={a.owner_initials} color={a.owner_color || "#003867"} src={a.owner_avatar_url} size="sm" className="h-6 w-6 shrink-0" />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="px-6 py-14 text-center" data-testid="customers-empty">
            <Building2 className="mx-auto h-6 w-6 text-muted-foreground" />
            <div className="mt-3 text-[14px] font-medium">{q ? `Ingen kunder matcher «${q}»` : "Ingen kunder ennå"}</div>
            <p className="mx-auto mt-1 max-w-[380px] text-[13px] text-muted-foreground">Kunder opprettes automatisk når en forespørsel struktureres – eller legg til manuelt.</p>
          </div>
        )}
      </div>
      <NewCustomerDialog open={open} onOpenChange={setOpen} onCreated={(a) => { load(); navigate(`/customers/${a.id}`); }} />
    </div>
  );
}
